import {
  ComponentProps,
  ComponentType,
  FC,
  ReactNode,
  createRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TinderCard from 'react-tinder-card';

import { map } from 'cypress/types/bluebird';

import { useBadgeById } from '@queries/CritiqueQueries';
import { FeedPost, useFeaturedLooksFeedPostsQuery } from '@queries/PostQueries';

import { useSendEntertainerBadge } from '@mutations/EntertainerMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCurrentUserCity } from '@hooks/useCurrentUserLocation';

import { useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { SendEntertainerBadgeButton } from '@components/buttons/SendEntertainerBadgeButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import Card from '@components/layout/Card/Card';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { Screen } from '@components/layout/Screen';

interface SwiperScreenContentProps<T> {
  cardData: T[];
  containerHeight: number;
  HeaderContent?: ComponentType<{ containerRef: any }>;
  CardContent: ComponentType<{ data: T; cardHeight: number; onCardContentInteraction: () => void }>;
  onSwipeRight: (data: T) => void;
  onSwipeLeft: (data: T) => void;
  emptyState: ReactNode;
  onFetchMore?: () => void;
}

let canClickTimer: NodeJS.Timeout | null = null;

const SwiperScreenContent = <T extends { key: string }>({
  cardData,
  containerHeight,
  HeaderContent,
  CardContent,
  onSwipeRight,
  onSwipeLeft,
  emptyState,
  onFetchMore,
}: SwiperScreenContentProps<T>) => {
  const [canClick, setCanClick] = useState(true);
  const [swipedCards, setSwipedCards] = useState<Record<string, boolean>>({});
  const headerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useMemo(
    () => Object.fromEntries(cardData.map(({ key }) => [key, createRef<any>()])),
    [cardData],
  );

  const [headerHeight, setHeaderHeight] = useState(0);
  useLayoutEffect(() => {
    if (headerRef.current?.clientHeight) {
      const { firstChild } = headerRef.current;

      if (firstChild instanceof HTMLElement) {
        setHeaderHeight(firstChild.clientHeight);
      }
    }
  }, []);

  const handleCardLeftScreen = (data: T) => {};

  const handleSwipe = (direction: string, data: T) => {
    if (canClickTimer) {
      clearTimeout(canClickTimer);
    }

    if (direction === 'right') {
      onSwipeRight(data);
    } else if (direction === 'left') {
      onSwipeLeft(data);
    }

    setSwipedCards((prev) => ({ ...prev, [data.key]: true }));
    setCanClick(false);

    canClickTimer = setTimeout(() => {
      setCanClick(true);
    }, 2000);
  };

  const remainingCards = useMemo(
    () => cardData.filter(({ key }) => !swipedCards[key]),
    [cardData, swipedCards],
  );
  const topCard = remainingCards?.[remainingCards?.length - 1];

  useEffect(() => {
    if (remainingCards.length === 0 && onFetchMore) {
      onFetchMore();
    }
  }, [remainingCards]);

  const handleSkip = () => {
    cardRefs[topCard?.key ?? '']?.current?.swipe('left');
  };

  const handleLike = () => {
    cardRefs[topCard?.key ?? '']?.current?.swipe('right');
  };

  const VERTICAL_PADDING = 16 * 2;
  const ICON_BUTTON_CONTAINER = 48;
  const EXTRA_PADDING = 16;

  return (
    <div
      style={{
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
        padding: '1rem 0',
        display: 'grid',
        gridTemplateRows: 'auto auto 1fr',
      }}
    >
      <HeaderContent containerRef={headerRef} />

      {remainingCards.length ? (
        <>
          <div style={{ position: 'relative' }}>
            {remainingCards.map((data) => (
              <div
                key={data.key}
                style={{ position: 'absolute', width: '100%', padding: '0 1rem' }}
              >
                <TinderCard
                  ref={cardRefs[data.key]}
                  onSwipe={(direction) => handleSwipe(direction, data)}
                  onCardLeftScreen={() => handleCardLeftScreen(data)}
                  preventSwipe={['up', 'down']}
                >
                  <CardContent
                    data={data}
                    cardHeight={
                      containerHeight -
                      VERTICAL_PADDING -
                      ICON_BUTTON_CONTAINER -
                      EXTRA_PADDING -
                      headerHeight
                    }
                    onCardContentInteraction={() => {
                      setSwipedCards((prev) => ({ ...prev, [data.key]: true }));
                    }}
                  />
                </TinderCard>
              </div>
            ))}
          </div>
          <div className="mt-auto">
            <div className="d-flex gap-3 justify-content-center">
              <div>
                <IconButton
                  disabled={!canClick}
                  color="neutral"
                  variant="smooth"
                  size="lg"
                  onClick={handleSkip}
                >
                  <div style={{ color: 'var(--color-red)' }}>
                    <FontAwesomeIcon name="times" />
                  </div>
                </IconButton>
              </div>
              <div>
                <IconButton
                  disabled={!canClick}
                  color="neutral"
                  variant="smooth"
                  size="lg"
                  onClick={handleLike}
                >
                  <div style={{ color: 'var(--color-green)' }}>
                    <FontAwesomeIcon name="heart" faStyle="far" />
                  </div>
                </IconButton>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="px-3">{emptyState}</div>
      )}
    </div>
  );
};

const LookCardContent: FC<{
  data: FeedPost & { key: string };
  cardHeight: number;
  onCardContentInteraction: () => void;
}> = ({ data: post, cardHeight, onCardContentInteraction }) => {
  const [hasError, setHasError] = useState(false);
  const TITLE_CONTAINER_HEIGHT = 80;
  const BADGE_CONTAINER_HEIGHT = 0; // 90;

  return (
    <div>
      <Card>
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            height: cardHeight - TITLE_CONTAINER_HEIGHT - BADGE_CONTAINER_HEIGHT,
            overflow: 'hidden',
            position: 'relative',
            padding: '1rem',
          }}
        >
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backgroundImage: `url(${post.entertainerMedia.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center center',
              filter: 'blur(8px)',
              opacity: 0.8,
            }}
          />
          <AsyncImage
            style={{
              height: '100%',
              width: '100%',
              position: 'relative',
              zIndex: 1,
              objectFit: 'contain',
              display: 'block',
              borderRadius: 8,
            }}
            src={post.entertainerMedia.image}
            onError={() => {
              console.log('error');
            }}
          />
        </div>
        <div
          className="d-flex align-items-center py-3 px-2"
          style={{ height: TITLE_CONTAINER_HEIGHT }}
        >
          <div>
            <p
              className="my-0"
              style={{
                textTransform: 'uppercase',
                letterSpacing: '.1rem',
                fontWeight: 500,
                opacity: 0.7,
                fontSize: '.7rem',
              }}
            >
              {post.entertainer?.title}
            </p>
            <h3 className="my-0">{post.entertainerMedia?.title}</h3>
          </div>
        </div>
      </Card>
    </div>
  );
};

interface PersonalizedBadgeSwiperScreenProps {
  badgeId?: string;
}

export const PersonalizedBadgeSwiperScreen =
  createScreenComponent<PersonalizedBadgeSwiperScreenProps>(({ badgeId }) => {
    const screenContentRef = useRef<{ el: HTMLElement | null }>(null);
    const { track } = useAnalytics();
    const { navigate } = useRouter();
    const { currentCity } = useCurrentUserCity();
    const {
      postsAreReady,
      posts = [],
      refetchPosts,
    } = useFeaturedLooksFeedPostsQuery({
      cityId: currentCity?._id,
    });
    const { sendEntertainerBadgeAsync, isSubmitting } = useSendEntertainerBadge();
    const { badge } = useBadgeById(badgeId);

    const [containerHeight, setContainerHeight] = useState<number>();
    useLayoutEffect(() => {
      if (screenContentRef.current?.el?.clientHeight) {
        const { firstChild } = screenContentRef.current.el;

        if (firstChild instanceof HTMLElement) {
          setContainerHeight(firstChild.clientHeight);
        }
      }
    }, []);

    const cardData = useMemo(() => {
      return posts.map((post) => ({ ...post, key: post.entertainerMedia._id }));
    }, [posts]);

    const handleSwipe = (direction: string) => (post: FeedPost & { key: string }) => {
      track(`Swipe ${direction}`, {
        entertainerMediaId: post.entertainerMedia._id,
      });

      sendEntertainerBadgeAsync({
        badgeId,
        entertainerId: post.entertainer._id,
        entertainerMediaId: post.entertainerMedia._id,
        reason: direction === 'right' ? 'Look' : 'skip',
      });
    };

    const handleFetchMore = () => {
      if (postsAreReady) {
        refetchPosts();
      }
    };

    const HeaderContent: FC<{ containerRef: any }> = ({ containerRef }) => {
      return (
        <div ref={containerRef}>
          <div className="pb-2">
            <div className="d-flex px-3 gap-2">
              <div>
                <AsyncImage
                  src={badge?.imageUrl}
                  placeholder={<div style={{ height: 48, width: 48 }} />}
                  style={{ height: 48, width: 48, display: 'block' }}
                />
              </div>
              <div>
                <div>
                  <strong className="fs-6">{badge?.title}</strong>
                  <br />
                  <span>Tap ❌ to skip or ❤️ to award your badge</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <Screen
        name="Entertainer Looks Swiper Screen"
        hideToolbar
        disableLocationPermissionGate={false}
        headerProps={{
          title: 'Share Your Badge',
        }}
      >
        <Screen.Content ref={screenContentRef} maxWidth={425} screenContentClassName="p-0">
          <SwiperScreenContent
            cardData={cardData}
            containerHeight={containerHeight}
            HeaderContent={HeaderContent}
            CardContent={LookCardContent}
            onSwipeLeft={handleSwipe('left')}
            onSwipeRight={handleSwipe('right')}
            emptyState={
              <EmptyState
                title="No more looks"
                text="You have swiped through all the available looks."
              />
            }
            onFetchMore={handleFetchMore}
          />
        </Screen.Content>
      </Screen>
    );
  });
